import React, { useEffect, useRef, useState } from "react";
import "./ThemedAudioPlayer.css";

function formatAudioTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const roundedSeconds = Math.floor(seconds);
  const hours = Math.floor(roundedSeconds / 3600);
  const minutes = Math.floor((roundedSeconds % 3600) / 60);
  const remainingSeconds = roundedSeconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`
    : `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export default function ThemedAudioPlayer({ src, title, onEnded, onError }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [storyAudioCompleted, setStoryAudioCompleted] = useState(false);
  const [audioLoadError, setAudioLoadError] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setStoryAudioCompleted(false);
    setAudioLoadError(false);
    setIsBuffering(false);
  }, [src]);

  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio || audioLoadError) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch (error) {
        setAudioLoadError(true);
        onError?.(error);
      }
    } else {
      audio.pause();
    }
  };

  const handleSeek = (event) => {
    const nextTime = Number(event.target.value);
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(nextTime)) return;
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(duration);
    setStoryAudioCompleted(true);
    onEnded?.();
  };

  const handleError = (error) => {
    setIsPlaying(false);
    setAudioLoadError(true);
    onError?.(error);
  };

  return (
    <div className="themed-audio-player">
      <div className="themed-audio-player__header">
        <div className="themed-audio-player__label">{title}</div>
      </div>
      <audio
        ref={audioRef}
        className="themed-audio-player__media"
        preload="metadata"
        src={src}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsBuffering(true)}
        onCanPlay={() => setIsBuffering(false)}
        onEnded={handleEnded}
        onError={handleError}
      />
      <div className="themed-audio-player__controls">
        <button
          type="button"
          className="themed-audio-player__play-button"
          aria-label={isPlaying ? `Pause ${title}` : `Play ${title}`}
          onClick={handlePlayPause}
          disabled={audioLoadError}
        >
          <span aria-hidden="true">{isPlaying ? "Ⅱ" : "▶"}</span>
        </button>
        <div className="themed-audio-player__timeline">
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={Math.min(currentTime, duration || 0)}
            aria-label={`Seek ${title}`}
            disabled={!duration || audioLoadError}
            onChange={handleSeek}
          />
          <div className="themed-audio-player__time" aria-live="off">
            <span>{formatAudioTime(currentTime)}</span>
            <span>{formatAudioTime(duration)}</span>
          </div>
        </div>
      </div>
      {audioLoadError || storyAudioCompleted || isBuffering ? (
        <div className="themed-audio-player__status" role="status">
          {audioLoadError
            ? "The audio could not be loaded. Please read the passage below to continue."
            : storyAudioCompleted
              ? "Audio complete. Now scroll through the full passage below to continue."
              : "Loading audio…"}
        </div>
      ) : null}
    </div>
  );
}
